import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { LISTING_STATUS, CreateListingInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const ListingRow = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(LISTING_STATUS),
  kind: z.string(),
  title: z.unknown().nullable(),
  city: z.string().nullable(),
  zone: z.string().nullable(),
  province: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  published_snapshot_id: z.string().uuid().nullable(),
  cover_url: z.string().nullable().optional(),
  prezzo: z.number().nullable().optional(),
  ape: z.string().nullable().optional(),
  contract: z.string().nullable().optional(),
  property_type: z.string().nullable().optional(),
  compliance: z.object({
    alert: z.boolean(),
    missing_blocking: z.number(),
    expired: z.number(),
    expiring_soon: z.number(),
  }).nullable().optional(),
  keys: z.object({
    location: z.string(),
    out: z.boolean(),
  }).nullable().optional(),
});
export type ListingRow = z.infer<typeof ListingRow>;

const ListingsResponse = z.object({
  items: z.array(ListingRow),
  total: z.number(),
  page: z.number(),
  has_more: z.boolean(),
});

export type ListingsFilters = {
  status?: string;
  city?: string;
  q?: string;
  page?: number;
  page_size?: number;
};

export function useListings(filters: ListingsFilters = {}) {
  const { session } = useAuth();
  const token = session?.access_token;

  return useQuery({
    queryKey: ["listings", filters],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/listings", token!, ListingsResponse, { query: filters }),
  });
}

const CreatedListing = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  status: z.enum(LISTING_STATUS),
  created_at: z.string(),
});

export function useCreateListing() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;

  return useMutation({
    mutationFn: (input: z.infer<typeof CreateListingInput>) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch("/api/v1/admin/listings", token, CreatedListing, { method: "POST", body: input });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["listings"] }); },
  });
}
